/**
 * OutgoFactorableBadge
 *
 * The Outgo "$" factorability mark — a scalloped seal badge with a knockout dollar
 * sign, shown on a load when the user's selected factoring provider is Outgo and the
 * load is factorable (`is_factorable === true`). Not-factorable / no-info render nothing.
 *
 * Web treatment (per design): monochrome — the badge body is `currentColor` (slate-900
 * in light mode, slate-100 in dark) and the "$" is knocked out to the surface color, so
 * it stays legible in both themes. Vector geometry is lifted from the Figma source
 * (Spot / node 6104-76152); only the fills are swapped for the mono treatment.
 */
import { cn } from 'utils/cn'
import { Tooltip } from 'components/ds'

// Web tooltip copy. (Mobile has its own via the i18n key dispatcher.factoring.factorableTooltip.)
const OUTGO_FACTORABLE_TOOLTIP = 'This load is factorable with Outgo'

interface OutgoFactorableBadgeProps {
    /** Pixel size of the square badge. Matches the rate text line-height (16). */
    size?: number
    className?: string
    /** Wrap in a hover tooltip. Off when a parent already labels it (e.g. the detail chip). */
    withTooltip?: boolean
}

export const OutgoFactorableBadge = ({ size = 16, className, withTooltip = true }: OutgoFactorableBadgeProps) => {
    const mark = (
        <span
            className={cn('inline-flex shrink-0 items-center text-slate-900 dark:text-slate-100', className)}
            aria-label={OUTGO_FACTORABLE_TOOLTIP}
            role="img"
        >
            <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Scalloped seal badge body — currentColor so it flips with the theme. */}
                <path
                    fill="currentColor"
                    d="M7.34307 18.2209L5.85599 15.7147L3.03224 15.0959L3.30786 12.189L1.39453 9.99985L3.30786 7.81069L3.03224 4.90381L5.85599 4.28506L7.34307 1.77881L10.0002 2.90694L12.6573 1.77881L14.1444 4.28506L16.9681 4.90381L16.6925 7.81069L18.6058 9.99985L16.6925 12.189L16.9681 15.0959L14.1444 15.7147L12.6573 18.2209L10.0002 17.0928L7.34307 18.2209Z"
                />
                {/* Dollar-sign glyph knocked out to the surface color (white / slate-900 in dark). */}
                <path
                    className="fill-white dark:fill-slate-900"
                    d="M9.41304 14.5835H10.7174V13.5822H11.7609C11.9783 13.5822 12.1558 13.4932 12.2935 13.3152C12.4312 13.1373 12.5 12.937 12.5 12.7145V10.1616C12.5 9.92043 12.4312 9.71455 12.2935 9.54397C12.1558 9.37339 11.9783 9.28811 11.7609 9.28811H8.80435V7.75291H12.5V6.41796H10.7174V5.41675H9.41304V6.41796H8.34783C8.13044 6.41796 7.93478 6.50696 7.76087 6.68495C7.58696 6.86294 7.5 7.07149 7.5 7.3106V9.84167C7.5 10.0808 7.58696 10.2708 7.76087 10.4117C7.93478 10.5526 8.13044 10.6231 8.34783 10.6231H11.1957V12.2473H7.5V13.5822H9.41304V14.5835Z"
                />
            </svg>
        </span>
    )
    return withTooltip ? <Tooltip title={OUTGO_FACTORABLE_TOOLTIP}>{mark}</Tooltip> : mark
}
